import { useState, useEffect } from 'react'

export interface UserContext {
  id: string
  name: string
  role: string
  department: string
  defaultDimensions?: {
    [dimensionName: string]: string
  }
}

// Mock user data - in production this would come from auth/backend
const MOCK_USERS: Record<string, UserContext> = {
  'it-manager': {
    id: 'user-001',
    name: 'Sarah Chen',
    role: 'IT Manager',
    department: 'IT',
    defaultDimensions: {
      'Business Unit': 'BU05', // Technology
      Department: '400', // IT
      'Cost Centre': '4010', // Infrastructure
      Location: 'LOC-HQ', // Headquarters
      Project: 'P006', // Infrastructure Upgrade
      Activity: 'A400', // Hardware
      'Product Line': 'PL-TEC', // Technology
      Customer: 'INTERNAL', // Internal Use
      Region: 'GLOBAL',
    },
  },
  'finance-manager': {
    id: 'user-002',
    name: 'Michael Rodriguez',
    role: 'Finance Manager',
    department: 'Finance',
    defaultDimensions: {
      'Business Unit': 'BU01', // Corporate
      Department: '100', // Finance
      'Cost Centre': '1010', // Accounting
      Location: 'LOC-HQ', // Headquarters
      Project: 'P001', // ERP Implementation
      Activity: 'A200', // Consulting
      'Product Line': 'PL-FIN', // Financial Services
      Customer: 'INTERNAL', // Internal Use
      Region: 'GLOBAL',
    },
  },
  'operations-manager': {
    id: 'user-003',
    name: 'Lisa Thompson',
    role: 'Operations Manager',
    department: 'Operations',
    defaultDimensions: {
      'Business Unit': 'BU02', // Manufacturing
      Department: '200', // Operations
      'Cost Centre': '2010', // Manufacturing
      Location: 'LOC-CHI', // Chicago
      Project: 'P003', // Product Launch
      Activity: 'A100', // Salaries
      'Product Line': 'PL-MFG', // Manufacturing
      Customer: 'INTERNAL', // Internal Use
      Region: 'NA-US',
    },
  },
}

const CURRENT_USER_KEY = 'gl_coding_current_user'

export function useUserContext() {
  const [currentUser, setCurrentUser] = useState<UserContext | null>(null)

  useEffect(() => {
    // Load current user from localStorage or default to IT Manager for demo
    const savedUserId = localStorage.getItem(CURRENT_USER_KEY) || 'it-manager'
    const user = MOCK_USERS[savedUserId]
    if (user) {
      setCurrentUser(user)
    }
  }, [])

  const switchUser = (userId: string) => {
    const user = MOCK_USERS[userId]
    if (user) {
      setCurrentUser(user)
      localStorage.setItem(CURRENT_USER_KEY, userId)
    }
  }

  return {
    currentUser,
    switchUser,
    availableUsers: Object.values(MOCK_USERS),
  }
}
